import { useLocalStorage } from "@rehooks/local-storage";
import { useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";

import { getSortOptionFromURI } from "components/Filters/helpers";
import {
  EmptystateSearchNoResultsColored,
  EmptystateStacksColored,
} from "components/icons/generated";
import ListContentWrapper from "components/ListContentWrapper";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import SearchInput from "components/SearchInput";
import { getSearchQuery } from "components/SearchInput/helpers";
import TablePagination from "components/Table/Pagination";
import { PAGINATION_OPTIONS } from "components/Table/Pagination/constants";
import usePagination from "components/Table/Pagination/usePagination";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import Table from "ds/components/Table";
import TableBody from "ds/components/Table/Body";
import TableSkeleton from "ds/components/Table/Skeleton";
import TableWrapper from "ds/components/Table/Wrapper";
import TableSimpleHead from "ds/components/TableSimple/Head";
import Toggle from "ds/components/Toggle";
import TooltipInfo from "ds/components/TooltipInfo";
import useErrorHandle from "hooks/useErrorHandle";
import useHasSpaceAdminAccess from "shared/Space/useHasSpaceAdminAccess";
import { SearchQueryOrderDirection, StackAccessMode } from "types/generated";

import {
  SPACE_STACKS_FILTERS_ORDER_KEY,
  SPACE_STACKS_INCLUDE_OTHER_SPACES_URL_PARAM,
  SPACE_STACKS_INITIAL_LIMIT,
  SPACE_STACKS_INITIAL_ORDER_BY,
  SPACE_STACKS_LIMIT_STORAGE_KEY,
  SPACE_STACKS_SORT_CONFIG,
} from "./constants";
import SpaceDetailsStacksRows from "./Rows";
import useSpaceDetailsStacks from "./useSpaceDetailsStacks";
import useSpaceDetailsStacksTableStore from "./useStore";

type SpaceDetailsStacksProps = {
  spaceId: string;
};

const TABLE_ARIA_LABEL = "Space stacks table";

const SpaceDetailsStacks = ({ spaceId }: SpaceDetailsStacksProps) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedRowsPerPage] = useLocalStorage<number>(
    SPACE_STACKS_LIMIT_STORAGE_KEY,
    SPACE_STACKS_INITIAL_LIMIT
  );

  const first = storedRowsPerPage || SPACE_STACKS_INITIAL_LIMIT;

  const fullTextSearch = getSearchQuery(searchParams) || null;
  const orderBy = useMemo(
    () =>
      getSortOptionFromURI(
        searchParams,
        SPACE_STACKS_INITIAL_ORDER_BY.field,
        SearchQueryOrderDirection.Asc
      )[0] ?? SPACE_STACKS_INITIAL_ORDER_BY,
    [searchParams]
  );

  // ALL_WITH_ACCESS requires admin on the space (RESIDENT needs read only), so below admin the
  // toggle is disabled and a shared ?includeOtherSpaces URL is ignored.
  const hasSpaceAdminAccess = useHasSpaceAdminAccess(spaceId);

  const includeOtherSpaces =
    hasSpaceAdminAccess && searchParams.get(SPACE_STACKS_INCLUDE_OTHER_SPACES_URL_PARAM) === "true";
  const mode = includeOtherSpaces ? StackAccessMode.AllWithAccess : StackAccessMode.Residing;

  const columnIds = useSpaceDetailsStacksTableStore((state) => state.columnsOrder.visible);

  const {
    loadStacks,
    error,
    stacks,
    initialLoading,
    loadingContent,
    totalCount,
    endCursor,
    startCursor,
    paginationResetKey,
  } = useSpaceDetailsStacks({ spaceId, orderBy, first, fullTextSearch, mode });

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: loadStacks,
    endCursor,
    startCursor,
    allItemsCount: totalCount,
    visibleItemsCount: stacks.length,
    initialLimit: SPACE_STACKS_INITIAL_LIMIT,
    storageKey: SPACE_STACKS_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const handleIncludeOtherSpacesChange = useCallback(
    (isSelected: boolean) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (isSelected) {
            next.set(SPACE_STACKS_INCLUDE_OTHER_SPACES_URL_PARAM, "true");
          } else {
            next.delete(SPACE_STACKS_INCLUDE_OTHER_SPACES_URL_PARAM);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return <PageLayoutSkeleton />;
  }

  return (
    <>
      <PageInfoHeaderWrapper>
        <PageInfoHeaderTitle title="Stacks" />
        <PageInfoHeaderActions>
          <SearchInput
            placeholder="Search by stack name"
            filtersOrderSettingsKey={SPACE_STACKS_FILTERS_ORDER_KEY}
          />
          <Box align="center" gap="small">
            <Toggle
              variant="switch"
              id="space-stacks-include-other-spaces"
              isSelected={includeOtherSpaces}
              onChange={handleIncludeOtherSpacesChange}
              typographyVariant="p-t8"
              isDisabled={!hasSpaceAdminAccess}
              tooltip={
                hasSpaceAdminAccess ? undefined : () => "Requires admin access to this space"
              }
            >
              Include all stacks
            </Toggle>
            <TooltipInfo variant="standard" widthMode="maxWidthSm">
              Show stacks with access to this space residing in other spaces
            </TooltipInfo>
          </Box>
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>

      <ListContentWrapper>
        {!stacks.length && !fullTextSearch && (
          <EmptyState
            title="This space has no stacks yet"
            icon={EmptystateStacksColored}
            caption="Stacks with access to this space will show up here."
          />
        )}

        {!stacks.length && !!fullTextSearch && (
          <EmptyState
            title="No stacks found"
            icon={EmptystateSearchNoResultsColored}
            caption="No stacks match your search."
          />
        )}

        {!!stacks.length && (
          <>
            <TableWrapper ref={tableRef} hasPagination>
              <Table ariaLabel={TABLE_ARIA_LABEL}>
                <TableSimpleHead
                  store={useSpaceDetailsStacksTableStore}
                  ariaLabel={TABLE_ARIA_LABEL}
                  tableRef={tableRef}
                  sortConfig={SPACE_STACKS_SORT_CONFIG}
                  filtersStorageKey={SPACE_STACKS_FILTERS_ORDER_KEY}
                  hideSettings
                  stickyRight={() => false}
                />
                {loadingContent && (
                  <TableSkeleton columnIds={columnIds} rowsPerPage={rowsPerPage} />
                )}
                {!loadingContent && (
                  <TableBody>
                    <SpaceDetailsStacksRows
                      data={stacks}
                      store={useSpaceDetailsStacksTableStore}
                      tableRef={tableRef}
                    />
                  </TableBody>
                )}
              </Table>
            </TableWrapper>
            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={totalCount}
              lastPage={lastPage}
              page={page}
              paginationOptions={PAGINATION_OPTIONS}
            />
          </>
        )}
      </ListContentWrapper>
    </>
  );
};

export default SpaceDetailsStacks;
